import axios, { AxiosError, AxiosRequestConfig } from 'axios';

import { httpClient } from 'services/httpClient';

/**
 * Normalised error thrown by every helper in this module.
 * `message` is taken from the gateway/backend response body when available.
 */
export class ApiError extends Error {
  status?: number;

  code?: string;

  data?: unknown;

  constructor(message: string, status?: number, code?: string, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.data = data;
  }
}

function extractMessage(data: unknown): { message?: string; code?: string } {
  if (typeof data === 'string' && data.trim()) return { message: data };
  if (!data || typeof data !== 'object') return {};
  const body = data as Record<string, any>;
  const nested =
    body.error && typeof body.error === 'object' ? body.error : undefined;
  const code = nested?.code ?? body.code;
  let message: unknown = nested?.message ?? body.message;
  if (Array.isArray(message)) message = message.join(', ');
  if (typeof message !== 'string' && typeof body.error === 'string') {
    message = body.error;
  }
  return {
    message: typeof message === 'string' ? message : undefined,
    code: typeof code === 'string' ? code : undefined
  };
}

export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  if (axios.isAxiosError(error)) {
    const { response } = error as AxiosError<unknown>;
    const { message, code } = extractMessage(response?.data);
    return new ApiError(
      message || response?.statusText || error.message || 'Request failed',
      response?.status,
      code,
      response?.data
    );
  }
  return new ApiError(
    error instanceof Error ? error.message : 'Request failed'
  );
}

async function call<T>(request: Promise<{ data: T }>): Promise<T> {
  try {
    const res = await request;
    return res.data;
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * Typed helpers over the shared gateway `httpClient` (base URL from
 * `GATEWAY_HOST` config, Bearer token attached automatically).
 */
export const api = {
  get: <T = unknown>(url: string, config?: AxiosRequestConfig) =>
    call<T>(httpClient.get<T>(url, config)),
  post: <T = unknown>(
    url: string,
    body?: unknown,
    config?: AxiosRequestConfig
  ) => call<T>(httpClient.post<T>(url, body, config)),
  patch: <T = unknown>(
    url: string,
    body?: unknown,
    config?: AxiosRequestConfig
  ) => call<T>(httpClient.patch<T>(url, body, config))
};

/** Activate or deactivate a product, supplier or location. */
export const setActivation = <T = unknown>(
  resource: 'products' | 'suppliers' | 'locations',
  id: number | string,
  isActivated: boolean
) =>
  api.post<T>(
    `/admin/${resource}/${id}/${isActivated ? 'activate' : 'deactivate'}`
  );
